import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import {
  getMlLog,
  getMlRun,
  getMlRunFileUrl,
  getMlRootCause,
  getMlRunSummary,
  listRunIds,
  pinMlRun,
  runMlModel,
} from "../api/ml";
import CsvFetcher from "../components/CsvFetcher";
import PredProbaHistogram from "../components/PredProbaHistogram";
import GoToDashboardButton from "../components/GoToDashboardButton";
import { usePredictions } from "../hooks/usePredictions";
import styles from "./MLRunDetail.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "rootCause", label: "Root Cause" },
  { id: "predictions", label: "Predictions" },
  { id: "patterns", label: "Failure Patterns" },
  { id: "logs", label: "Logs" },
];

export default function MLRunDetail() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { runId } = useParams();

  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [counts, setCounts] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingRun, setLoadingRun] = useState(false);
  const [runningModel, setRunningModel] = useState(false);
  const [error, setError] = useState("");

  const refreshRuns = useCallback(async () => {
    setLoadingRuns(true);
    setError("");

    try {
      const data = await listRunIds();
      const nextRuns = Array.isArray(data.runs) ? data.runs : [];
      setRuns(nextRuns);
      return nextRuns;
    } catch (err) {
      setError(err.message || t("Unable to load ML runs."));
      return [];
    } finally {
      setLoadingRuns(false);
    }
  }, [t]);

  useEffect(() => {
    refreshRuns();
  }, [refreshRuns]);

  useEffect(() => {
    if (!loadingRuns && !runId && runs.length > 0) {
      navigate(`/ml/runs/${runs[0].runId}`, { replace: true });
    }
  }, [loadingRuns, navigate, runId, runs]);

  useEffect(() => {
    if (!runId) {
      setSelectedRun(null);
      setCounts(null);
      return;
    }

    const loadRun = async () => {
      setLoadingRun(true);
      setError("");

      try {
        const [runData, summaryData] = await Promise.all([
          getMlRun(runId),
          getMlRunSummary(runId).catch(() => null),
        ]);

        setSelectedRun(runData.run);
        setCounts(summaryData?.counts || null);
      } catch (err) {
        setSelectedRun(null);
        setCounts(null);
        setError(err.message || t("Unable to load the selected ML run."));
      } finally {
        setLoadingRun(false);
      }
    };

    loadRun();
  }, [runId, t]);

  const handleRunModel = async () => {
    setRunningModel(true);
    setError("");

    try {
      const data = await runMlModel();
      await refreshRuns();

      if (data.run?.runId) {
        navigate(`/ml/runs/${data.run.runId}`);
      }
    } catch (err) {
      setError(err.message || t("ML run failed. Check the server logs for details."));
    } finally {
      setRunningModel(false);
    }
  };

  const handlePin = async () => {
    if (!selectedRun) return;

    try {
      const data = await pinMlRun(selectedRun.runId, !selectedRun.pinned);
      setSelectedRun(data.run);
      await refreshRuns();
    } catch (err) {
      setError(err.message || t("Unable to update pin state."));
    }
  };

  const handleRunSelect = (event) => {
    const nextRunId = event.target.value;

    if (nextRunId) {
      navigate(`/ml/runs/${nextRunId}`);
    }
  };

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>{t("ML Runs")}</h3>
          <p className={styles.subtitle}>
            {t("Train the Random Forest model, inspect predictions, and review fail patterns.")}
          </p>
        </div>

        <div className={styles.controls}>
          <GoToDashboardButton />

          <select
            className={styles.selectRun}
            value={runId || ""}
            onChange={handleRunSelect}
            disabled={loadingRuns || runs.length === 0}
          >
            <option value="">{t("Select a run")}</option>

            {runs.map((run) => (
              <option key={run.runId} value={run.runId}>
                {run.pinned ? `${t("Pinned")} - ` : ""}
                {run.runId}
              </option>
            ))}
          </select>

          <button
            onClick={handleRunModel}
            disabled={runningModel}
            className={styles.rerunButton}
          >
            {runningModel ? t("Running model...") : t("Run ML Model")}
          </button>
        </div>
      </div>

      {error && <div className={styles.errorBox}>{error}</div>}

      {!loadingRuns && runs.length === 0 && (
        <div className={styles.emptyMessage}>
          <div className={styles.emptyTitle}>{t("No ML runs yet.")}</div>
          <p>{t("Start the first run to train the model and generate prediction files.")}</p>
        </div>
      )}

      {loadingRun && <div className={styles.panel}>{t("Loading selected run...")}</div>}

      {selectedRun && !loadingRun && (
        <>
          <div className={styles.summary}>
            <SummaryBox title={t("Status")} value={selectedRun.status || t("unknown")} />
            <SummaryBox title={t("Total Sessions")} value={counts?.total ?? "-"} />
            <SummaryBox title={t("Predicted PASS")} value={counts?.passCount ?? "-"} />
            <SummaryBox title={t("Predicted FAIL")} value={counts?.failCount ?? "-"} />
            <SummaryBox title={t("Duration")} value={formatDuration(selectedRun.durationSec)} />
          </div>

          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ""
                  }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {t(tab.label)}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <OverviewTab run={selectedRun} onPin={handlePin} t={t} />
          )}

          {activeTab === "rootCause" && <RootCauseTab runId={selectedRun.runId} t={t} />}
          {activeTab === "predictions" && <PredictionsTab runId={selectedRun.runId} t={t} />}
          {activeTab === "patterns" && <PatternsTab runId={selectedRun.runId} t={t} />}
          {activeTab === "logs" && <LogsTab runId={selectedRun.runId} t={t} />}
        </>
      )}
    </section>
  );
}

function SummaryBox({ title, value }) {
  return (
    <div className={styles.summaryBox}>
      <div className={styles.summaryBoxTitle}>{title}</div>
      <div className={styles.summaryBoxValue}>{value}</div>
    </div>
  );
}

function OverviewTab({ run, onPin, t }) {
  const files = Array.isArray(run.files) ? run.files : [];

  return (
    <div className={styles.panel}>
      {run.status === "error" && (
        <div className={styles.errorBox}>
          {run.error || t("This run ended with an error.")}
        </div>
      )}

      <div className={styles.overviewGrid}>
        <Info label={t("Run ID")} value={run.runId} />
        <Info label={t("Started")} value={formatDate(run.startedAt)} />
        <Info label={t("Ended")} value={formatDate(run.endedAt)} />
        <Info label={t("Database")} value={run.dbPath} />
        <Info label={t("Artifacts")} value={run.artifactsDir} />
        <Info label={t("Predictions")} value={run.predictionsDir} />
      </div>

      <button onClick={onPin} className={styles.secondaryButton}>
        {run.pinned ? t("Unpin Run") : t("Pin Run")}
      </button>

      <h4 className={styles.sectionTitle}>{t("Generated Files")}</h4>

      {files.length ? (
        <div className={styles.fileList}>
          {files.map((file) => (
            <a
              key={file}
              href={getMlRunFileUrl(run.runId, file)}
              target="_blank"
              rel="noreferrer"
            >
              {file}
            </a>
          ))}
        </div>
      ) : (
        <p>{t("No files were recorded for this run.")}</p>
      )}
    </div>
  );
}

function RootCauseTab({ runId, t }) {
  const [causes, setCauses] = useState([]);
  const [method, setMethod] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadRootCause = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await getMlRootCause(runId);
        setCauses(Array.isArray(data.causes) ? data.causes : []);
        setMethod(data.method || "");
      } catch (err) {
        setCauses([]);
        setError(err.message || t("Unable to load root-cause analysis."));
      } finally {
        setLoading(false);
      }
    };

    loadRootCause();
  }, [runId, t]);

  if (loading) {
    return <div className={styles.panel}>{t("Finding likely root causes...")}</div>;
  }

  if (error) {
    return <div className={styles.errorBox}>{error}</div>;
  }

  if (!causes.length) {
    return <EmptyMessage />;
  }

  return (
    <div className={styles.panel}>
      <p className={styles.note}>{method}</p>

      <div className={styles.rootCauseList}>
        {causes.slice(0, 10).map((cause, index) => (
          <article key={cause.stepName || index} className={styles.rootCauseCard}>
            <div className={styles.rootCauseHeader}>
              <div>
                <span className={styles.rankBadge}>#{index + 1}</span>
                <h4>{cause.stepName}</h4>
              </div>

              <strong>{cause.rankScore}</strong>
            </div>

            <div className={styles.causeMetrics}>
              <span>
                {t("Branch")}: {cause.likelyBranch}
              </span>

              <span>
                {t("Predicted FAIL sessions")}: {cause.failCount}
              </span>

              {cause.topStation?.stationName && (
                <span>
                  {t("Station")}: {cause.topStation.stationName}
                </span>
              )}

              {cause.topTestspec?.Testspec && (
                <span>
                  {t("Testspec")}: {cause.topTestspec.Testspec}
                </span>
              )}

              {cause.topAteSwVersion?.AteSwVersion && (
                <span>
                  {t("AteSwVersion")}: {cause.topAteSwVersion.AteSwVersion}
                </span>
              )}
            </div>

            <ul className={styles.evidenceList}>
              {(cause.evidence || []).map((item, evidenceIndex) => (
                <li key={`${item}-${evidenceIndex}`}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
}

function PredictionsTab({ runId, t }) {
  const { rows, loading, error } = usePredictions(runId);
  const [labelFilter, setLabelFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];

    const query = search.trim().toLowerCase();

    return rows.filter((row) => {
      const label = Number(row.pred_label) === 1 ? "pass" : "fail";
      const matchesLabel = labelFilter === "all" || label === labelFilter;
      const matchesSearch =
        !query || String(row.global_id || "").toLowerCase().includes(query);

      return matchesLabel && matchesSearch;
    });
  }, [labelFilter, rows, search]);

  if (loading) {
    return <div className={styles.panel}>{t("Loading predictions...")}</div>;
  }

  if (error) {
    return <div className={styles.errorBox}>{error.message}</div>;
  }

  return (
    <div className={styles.panel}>
      <PredProbaHistogram runId={runId} />

      <div className={styles.filters}>
        <select value={labelFilter} onChange={(event) => setLabelFilter(event.target.value)}>
          <option value="all">{t("All labels")}</option>
          <option value="pass">{t("Predicted PASS")}</option>
          <option value="fail">{t("Predicted FAIL")}</option>
        </select>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("Search global ID")}
        />
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("Global ID")}</th>
              <th>{t("Predicted Label")}</th>
              <th>{t("PASS Probability")}</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.slice(0, 200).map((row, index) => (
              <tr key={`${row.global_id}-${index}`}>
                <td>{row.global_id}</td>
                <td>{Number(row.pred_label) === 1 ? t("PASS") : t("FAIL")}</td>
                <td>{formatProbability(row.pred_proba_pass)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.note}>
        {t("Showing {{shown}} of {{total}} matching rows.", {
          shown: Math.min(filteredRows.length, 200),
          total: filteredRows.length,
        })}
      </p>
    </div>
  );
}

function PatternsTab({ runId, t }) {
  return (
    <div className={styles.panel}>
      <CsvFetcher runId={runId} fileName="predicted_fail_step_patterns.csv">
        {({ rows }) => <StepPatternChart rows={rows} t={t} />}
      </CsvFetcher>
    </div>
  );
}

function StepPatternChart({ rows, t }) {
  const topRows = useMemo(
    () =>
      [...rows]
        .filter((row) => Number(row.fail_count) > 0)
        .sort((a, b) => Number(b.fail_count) - Number(a.fail_count))
        .slice(0, 20),
    [rows]
  );

  if (!topRows.length) {
    return <EmptyMessage />;
  }

  return (
    <>
      <h4 className={styles.sectionTitle}>{t("Top Predicted FAIL Steps")}</h4>

      <div className={styles.chartBox}>
        <Bar
          data={{
            labels: topRows.map((row) => row.stepName),
            datasets: [
              {
                label: t("Fail count"),
                data: topRows.map((row) => Number(row.fail_count) || 0),
                backgroundColor: "rgba(31, 119, 180, 0.72)",
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
              legend: {
                display: false,
              },
            },
          }}
        />
      </div>

      <ul className={styles.failList}>
        {topRows.map((row) => (
          <li key={row.stepName}>
            <span>{row.stepName}</span>
            <strong>{row.fail_count}</strong>
          </li>
        ))}
      </ul>
    </>
  );
}

function LogsTab({ runId, t }) {
  const [kind, setKind] = useState("train");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadLog = async () => {
      setLoading(true);
      setError("");

      try {
        setText(await getMlLog(runId, kind));
      } catch (err) {
        setText("");
        setError(err.message || t("Unable to load log."));
      } finally {
        setLoading(false);
      }
    };

    loadLog();
  }, [kind, runId, t]);

  return (
    <div className={styles.panel}>
      <div className={styles.filters}>
        <button
          className={`${styles.secondaryButton} ${kind === "train" ? styles.activeSmall : ""}`}
          onClick={() => setKind("train")}
        >
          {t("Train Log")}
        </button>

        <button
          className={`${styles.secondaryButton} ${kind === "predict" ? styles.activeSmall : ""}`}
          onClick={() => setKind("predict")}
        >
          {t("Predict Log")}
        </button>
      </div>

      {loading && <p>{t("Loading log...")}</p>}

      {error && <div className={styles.errorBox}>{error}</div>}

      {!loading && !error && <pre className={styles.logBox}>{text || t("No log text.")}</pre>}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className={styles.infoItem}>
      <span>{label}</span>
      <strong title={value || ""}>{value || "-"}</strong>
    </div>
  );
}

function EmptyMessage() {
  const { t } = useTranslation();

  return (
    <div className={styles.emptyMessage}>
      <div className={styles.emptyTitle}>{t("No predicted FAIL sessions in this run.")}</div>
      <p>{t("Try another run, or run the model again after uploading more production data.")}</p>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";

  return new Date(value).toLocaleString();
}

function formatDuration(value) {
  if (!Number.isFinite(Number(value))) return "-";

  return `${value}s`;
}

function formatProbability(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) return "-";

  return `${(number * 100).toFixed(1)}%`;
}